import React from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { StyledEngineProvider, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import UserSettingsService, { CommonSettings } from "@services/userSettingsService";

import { darkTheme, lightTheme } from "./theme";

type ThemeMode = CommonSettings["themeMode"];

const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

const useBrowserTheme = () => {
    const [themeMode, setThemeMode] = React.useState<ThemeMode>(mediaQuery.matches ? "dark" : "light");

    const onChangeThemeMode = (e: MediaQueryListEvent) => {
        setThemeMode(e.matches ? "dark" : "light");
    };

    React.useEffect(() => {
        mediaQuery.addEventListener("change", onChangeThemeMode);

        return () => {
            mediaQuery.removeEventListener("change", onChangeThemeMode);
        };
    }, []);

    return themeMode;
};

type ThemeContextType = {
    themeMode: ThemeMode;
    setThemeMode: (themeMode: ThemeMode) => void;
};

const ThemeContext = React.createContext<ThemeContextType>({
    themeMode: "browser",
    setThemeMode: () => {},
});

export const ThemeProvider = (props: React.PropsWithChildren<object>) => {
    const [themeMode, setThemeMode] = React.useState(UserSettingsService.getThemeMode());
    const browserThemeMode = useBrowserTheme();

    const onChangeThemeMode = (value: ThemeMode) => {
        setThemeMode(value);
        UserSettingsService.setCommonValue<ThemeMode>("themeMode", value);
    };

    const onGetThemeMode = () => {
        let mode = themeMode;

        if (themeMode === "browser") {
            mode = browserThemeMode;
        }

        if (mode === "dark") {
            return darkTheme;
        }

        return lightTheme;
    };

    React.useEffect(() => {
        const resolvedTheme = themeMode === "browser" ? browserThemeMode : themeMode;
        if (resolvedTheme === "light" || resolvedTheme === "dark") {
            document.documentElement.setAttribute("data-theme", resolvedTheme);
        }
    }, [themeMode, browserThemeMode]);

    return (
        <ThemeContext.Provider value={{ themeMode, setThemeMode: onChangeThemeMode }}>
            <StyledEngineProvider injectFirst>
                <MuiThemeProvider theme={onGetThemeMode()}>
                    <CssBaseline />
                    {props.children}
                </MuiThemeProvider>
            </StyledEngineProvider>
        </ThemeContext.Provider>
    );
};

export const useThemeMode = () => {
    const context = React.useContext(ThemeContext);
    if (!context) {
        throw new Error("useThemeMode must be used within a ThemeProvider");
    }
    return context;
};
